<template>
  <div>
    <div v-if="$i18n.locale === 'pe'">
      <Message message="" icon="coming_soon_cl.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'br'">
      <Message message="" icon="coming_soon_br.svg" :as-card="true" />
    </div>
    <div v-if="$i18n.locale === 'cl'" id="consignments-content">
      <div id="investors-consignments-card" class="card-item mt-6 date-filter">
        <InvestorConsignmentsFilters
          v-if="isLoadInvestors"
          v-model="filtersModal"
          :investors="investors"
          :message-result="messageResult()"
          :enable-excel-button="filteredConsignments.length > 0"
          @filter="filterConsignments"
          @excelButtonClicked="downloadExcel" />
        <div class="spinner-content" :class="isLoading ? 'is-loading' : 'is-hidden'">
          <div class="spinner">
            <div class="double-bounce1" />
            <div class="double-bounce2" />
          </div>
        </div>
        <div v-if="!filteredConsignments.length" class="message-info has-text-centered">
          <i class="fas fa-exclamation-circle is-pulled-left" />
          <div class="message-text">
            <span>
              No hay remesas que cumplan con los valores introducidos en el filtro,
              vuelve a filtrar para obtener resultados.
            </span>
          </div>
        </div>
        <transition-group
          name="consignments-transition"
          class="consignments-group"
          appear
          tag="div">
          <investor-consignment
            v-for="(consignment, i) in filteredConsignments"
            :key="consignment._id"
            :investor="investors.find(investor => investor._id === consignment.investorId)"
            :consignment-data="consignment"
            :consignment-index="i"
            :current-extended-consignment="currentExtendedConsignment"
            :show-investors="investors.length > 1"
            :headers="excelHeaders"
            :style="{'--i': i}"
            @setCurrentExtendedConsignment="index => { currentExtendedConsignment = index; }" />
        </transition-group>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import zipcelx from 'zipcelx';
import { getConsignmentsByDate } from '@/application/services/api-clients/consignments';
import { getInvestorsByUser } from '@/application/services/api-clients/investors';
import { dateToFormatedString, now } from '@/infrastructure/date';
import InvestorConsignment from '@/ui/components/internal/investor/InvestorConsignment.vue';
import InvestorConsignmentsFilters from '@/ui/components/internal/investor/InvestorConsignmentsFilters.vue';
import { getFeatureToggleByName } from '@/application/services/api-clients/features-toggles';
import currentUser from '@/ui/mixins/current-user';
import notification from '@/ui/mixins/notification';
import locale from '@/ui/mixins/locale';
import { RutFormatter } from '@/domain/rut-formatter';
import Message from '@/ui/components/Message.vue';

export default {
  name: 'Consignments',
  components: {
    InvestorConsignment,
    InvestorConsignmentsFilters,
    Message
  },
  mixins: [
    currentUser,
    notification,
    locale
  ],
  data() {
    return {
      filtersModal: false,
      isLoading: true,
      isLoadInvestors: false,
      currentExtendedConsignment: undefined,
      consignments: [],
      filteredConsignments: [],
      investors: [],
      excelHeaders: {
        companyOperationId: 'CMF',
        debtor: 'Nombre deudor',
        idNumberDebtor: 'Rut deudor',
        paymentNumber: 'Nº de Cuota',
        dueDate: 'Vencimiento cuota',
        interest: 'Interés (UF)',
        amortization: 'Amortización (UF)',
        monthlyPayment: 'Monto cuota (UF)',
        lateInterest: 'Interés penal (UF)',
        consignedAmount: 'Monto remesado (UF)',
        paymentCreatedAt: 'Fecha de pago',
        secondaryCurrencyFactor: 'Valor UF',
        provider: 'Recaudador',
        bankAccount: 'Cuenta corriente',
        secondaryCurrencyConsignedAmount: 'Monto remesado (CLP)',
        consignmentCreatedAt: 'Fecha remesa'
      },
      ufDateState: false
    };
  },
  async mounted() {
    if (this.isChile()) {
      await this.loadInvestors();
      // Devuelve si el feature toggle está o no habilitado para el reporte en Excel y lo almacena en 'ufDateState'
      const featureToggleForConsignments = await Promise.all([getFeatureToggleByName('consignments')]);
      this.ufDateState = featureToggleForConsignments[0].isActive;
    }
  },
  methods: {
    messageResult() {
      if (this.consignments.length === 0) {
        return 'No hay registros para mostrar';
      }
      return `Hay ${this.consignments.length} remesas, estás viendo ${this.filteredConsignments.length}`;
    },
    async filterConsignments(filters) {
      const { selectedInvestorId, searchInputValue, from, to } = filters;
      await this.loadConsignments(from, to);
      this.filteredConsignments = this.consignments.filter(consignment => {
        if (selectedInvestorId && !selectedInvestorId.includes(consignment.investorId)) {
          return false;
        }
        if (searchInputValue) {
          const search = this.parseSearchString(searchInputValue);
          const notMatch = !this.parseSearchString(consignment._id).includes(search)
            && !this.parseSearchString(consignment.createdBy).includes(search)
            && !this.parseSearchString(consignment.totalSecondaryCurrencyConsignedAmount).includes(search)
            && !consignment.consignedPayments.some(payment => this.parseSearchString(payment.companyOperationId).includes(search))
            && !consignment.consignedPayments.some(payment => this.parseSearchString(payment.clientIdNumber).includes(search));
          if (notMatch) {
            return false;
          }
        }
        return true;
      });
    },
    parseSearchString: string => string.toString().toUpperCase().replace(/\./g, ''),
    async loadConsignments(from, to) {
      this.isLoading = true;
      try {
        this.consignments = await getConsignmentsByDate(from, to);
      } catch (_error) {
        this.alert({ message: 'Hubo un problema al tratar de obtener las remesas' });
      }
      this.isLoading = false;
    },
    async loadInvestors() {
      try {
        this.investors = await getInvestorsByUser(this.currentUser().user._id);
        this.isLoadInvestors = true;
      } catch (_error) {
        this.alert({ message: 'Hubo un problema al tratar de obtener los vehículos de inversión' });
      }
    },
    formatData(consignment, payment, bankAccount) {
      return {
        companyOperationId: payment.companyOperationId,
        debtor: `${payment.client.name} ${payment.client.lastName}`,
        idNumberDebtor: RutFormatter.pointAndDash(payment.clientIdNumber),
        paymentNumber: payment.paymentNumber,
        dueDate: dateToFormatedString(payment.dueDate, 'DD-MM-YYYY'),
        interest: payment.interest,
        amortization: payment.amortization,
        monthlyPayment: payment.monthlyPayment,
        lateInterest: payment.lateInterest,
        consignedAmount: payment.consignedAmount,
        paymentCreatedAt: dateToFormatedString(payment.createdAt, 'DD-MM-YYYY'),
        secondaryCurrencyFactor: payment.secondaryCurrencyFactor,
        provider: 'Creditú',
        bankAccount: Number(bankAccount),
        secondaryCurrencyConsignedAmount: payment.secondaryCurrencyConsignedAmount,
        consignmentCreatedAt: dateToFormatedString(consignment.createdAt, 'DD-MM-YYYY'),
        secondaryCurrencyDate: payment.secondaryCurrencyDate ? dateToFormatedString(payment.secondaryCurrencyDate, 'DD-MM-YYYY') : ''
      };
    },
    processDataForExcel() {
      const payments = [];
      let i = 0;
      this.filteredConsignments.forEach(consignment => {
        const bankAccount = this.investors.find(investor => investor._id === consignment.investorId).bankAccount.number;
        consignment.consignedPayments.forEach(payment => {
          payments.push(this.formatData(consignment, payment, bankAccount));
          // Recorre cada remesa pagada. Si está deshabilitado el feature toggle, entonces elimina la propiedad 'secondaryCurrencyDate'.
          if (!this.ufDateState) {
            delete payments[i].secondaryCurrencyDate;
          }
          i += 1;
        });
      });
      return payments;
    },
    downloadExcel() {
      // Habilita la cabecera 'Fecha UF' del reporte Excel si el feature toggle está habilitado
      if (this.ufDateState) {
        this.excelHeaders = {
          ...this.excelHeaders,
          secondaryCurrencyDate: 'Fecha UF'
        };
      }
      const rows = this.processDataForExcel();
      const headerRow = Object.values(this.excelHeaders);
      const config = {
        filename: `Remesas ${now('DD-MM-YY-HH-mm')}`,
        sheet: {
          data: [headerRow.map(value => ({
            value,
            type: 'string'
          }))]
        }
      };
      rows.forEach(x => {
        const values = Object.values(x);
        const objects = values.map(value => ({
          value,
          type: typeof value
        }));
        config.sheet.data.push(objects);
      });
      try {
        zipcelx(config);
        this.sendExcelAction();
      } catch (_error) {
        this.alert({ message: 'Hubo un problema al exportar el documento' });
      }
    },
    async sendExcelAction() {
      try {
        const { user } = this.currentUser();
        const action = {
          country: this.$i18n.locale,
          view: this.$route.name,
          user_id: user._id,
          user_rol: user.rol,
          executed_at: new Date(),
          action: 'excel'
        };
        if (user.rol === 'INVESTOR' && user.investorsIds) {
          action.metadata = { investorsIds: user.investorsIds };
        }
        // this.$socket.emit('action', action);
      } catch (error) {
        Vue.$log.error('Error al enviar action excel', error);
      }
    }
  }
};
</script>
<style scoped>
.message-text span:first-child {
  display: block;
  margin-bottom: 10px;
}

.message-info {
  display: grid;
  grid-template-columns: 1fr 10fr;
  gap: 1rem;
  align-items: center;
  background: #F2FBFB;
  padding: 20px;
  margin: 0.5rem 0 2rem 0;
  border-radius: 20px;
  font-family: var(--secondary-font);
  color: var(--color-nickel);
}

.message-info i {
  color: var(--primary);
}

.consignments-group > .card-item {
  margin-bottom: 20px;
}

.filters label {
  display: block;
}

.filters .select select {
  background-color: #FFF !important;
}

.filters .select select {
  font-size: 1.34em;
}

.spinner-content {
  position: absolute;
  background: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 3000;
}
.spinner-content .spinner {
  margin: 25% 50% !important;
}
</style>
